import { useEffect, useState } from "react";
import { siteGateAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import { Loader2, Eye, EyeOff } from "lucide-react";

const TOKEN_KEY = "site_gate_token";

export default function SiteGate({ children }) {
  const [state, setState] = useState({ loading: true, gated: false });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Strip zero-width and other invisible characters that privacy browsers /
  // clipboard-scrubbing extensions can silently inject.
  const sanitize = (v) => (v || "")
    .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
    .trim();

  const stored = () => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await siteGateAPI.status();
        if (!alive) return;
        if (!s.data?.enabled) {
          setState({ loading: false, gated: false });
          return;
        }
        const token = stored();
        if (!token) {
          setState({ loading: false, gated: true });
          return;
        }
        try {
          await siteGateAPI.check(token);
          setState({ loading: false, gated: false });
        } catch {
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
          setState({ loading: false, gated: true });
        }
      } catch {
        // Fail closed: if we can't reach the gate status endpoint (ad-blocker,
        // offline, DNS block, etc.), keep the site locked.
        if (alive) setState({ loading: false, gated: true });
      }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const clean = sanitize(password);
    if (!clean) return;
    setSubmitting(true);
    try {
      const r = await siteGateAPI.verify(clean);
      const token = r.data?.token;
      if (!token) throw new Error("no token");
      try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
      setPassword("");
      setState({ loading: false, gated: false });
    } catch (err) {
      setError(err.response?.data?.detail || "Incorrect password");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!state.gated) return children;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6" data-testid="site-gate">
      <svg aria-hidden viewBox="0 0 400 400" className="pointer-events-none absolute -right-24 top-0 h-[520px] w-[520px] text-primary/[0.06]">
        <path d="M60 380 L220 60 M120 380 L280 60 M180 380 L340 60 M240 380 L400 60" stroke="currentColor" strokeWidth="14" fill="none" strokeLinecap="round" />
      </svg>
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-start">
          <Logo size="lg" />
          <p className="mt-6 max-w-md text-sm leading-relaxed text-foreground/80">
            This site is currently in private testing. Enter the beta password to continue.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3 rounded-xl border border-border bg-card/70 p-5">
          <label htmlFor="site-gate-password" className="block text-[11px] font-bold uppercase tracking-wider text-foreground/80">
            Beta password
          </label>
          <div className="relative">
            <Input
              id="site-gate-password"
              type={showPassword ? "text" : "password"}
              autoFocus
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter beta password"
              className="pr-10 font-mono"
              data-testid="site-gate-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="site-gate-toggle-visibility"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {error && (
            <p className="text-xs text-primary" data-testid="site-gate-error">{error}</p>
          )}
          <Button
            type="submit"
            disabled={submitting || !password}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="site-gate-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "UNLOCK ACCESS"}
          </Button>
        </form>
      </div>
    </div>
  );
}
